import React, { useState, useCallback, useRef } from 'react';
import {
  Box, Typography, Button, IconButton, Chip, Tooltip,
  TextField, Divider, InputAdornment, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Paper,
  FormControl, Select, MenuItem, useTheme, useMediaQuery,
} from '@mui/material';
import {
  Search, Close as CloseIcon, Cancel, DeleteSweep, AccessTime,
} from '@mui/icons-material';
import { consistentSpinnerSx, LineFilter, LineFilterOperator, LINE_FILTER_OPTIONS, LINE_FILTER_ENABLED } from '../types';
import { buildSelectorString } from '../utils';
import { TimePickerPopover, TimePickerQuickRange } from './TimePickerPopover';
import apiClient from 'utils/ApiClient';

interface LabelBrowserDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (selections: Record<string, string[]>, lineFilter: LineFilter | null) => void;
  selectedServer: string;
  getTimeRangeMs: () => { start: number; end: number };
  initialSelections: Record<string, string[]>;
  initialLineFilter: LineFilter | null;
  allowedLabelValues?: Record<string, string[]>;
  /** Current toolbar time range label, shown on the in-dialog picker button. */
  timeRangeLabel: string;
  /**
   * Stable signature of the active time range. When it changes while the dialog
   * is open, labels/values are refetched for the new range.
   */
  timeRangeKey: string;
  /** Applies a quick range to the shared (toolbar) time range state. */
  onQuickRangeSelect: (range: TimePickerQuickRange) => void;
  /** Applies a custom absolute range to the shared (toolbar) time range state. */
  onApplyCustomRange: (fromMs: number, toMs: number, label: string) => void;
  /** Initial "From"/"To" values for the absolute picker, derived from current range. */
  getPickerInitialDates: () => { from: string; to: string };
  tzName: string;
  tzString: string;
}

export const LabelBrowserDialog: React.FC<LabelBrowserDialogProps> = React.memo(({
  open,
  onClose,
  onSubmit,
  selectedServer,
  getTimeRangeMs,
  initialSelections,
  initialLineFilter,
  allowedLabelValues,
  timeRangeLabel,
  timeRangeKey,
  onQuickRangeSelect,
  onApplyCustomRange,
  getPickerInitialDates,
  tzName,
  tzString,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [timeAnchorEl, setTimeAnchorEl] = useState<HTMLElement | null>(null);
  const prevTimeRangeKeyRef = useRef(timeRangeKey);
  const [allLabels, setAllLabels] = useState<string[]>([]);
  const [activeLabels, setActiveLabels] = useState<string[]>([]);
  const [labelValuesMap, setLabelValuesMap] = useState<Record<string, string[]>>({});
  const [originalLabelValues, setOriginalLabelValues] = useState<Record<string, string[]>>({});
  const [dialogSelectedValues, setDialogSelectedValues] = useState<Record<string, string[]>>({});
  const [disabledLabels, setDisabledLabels] = useState<string[]>([]);
  const [labelsLoading, setLabelsLoading] = useState(false);
  const [seriesLoading, setSeriesLoading] = useState(false);
  const [valueFilter, setValueFilter] = useState('');
  const lastModifiedLabelRef = useRef<string>('');
  const hasInitializedRef = useRef(false);
  const [lineFilterOperator, setLineFilterOperator] = useState<LineFilterOperator | ''>('');
  const [lineFilterValue, setLineFilterValue] = useState('');

  const currentLineFilter: LineFilter | null =
    LINE_FILTER_ENABLED && lineFilterOperator && lineFilterValue.trim()
      ? { operator: lineFilterOperator, value: lineFilterValue.trim() }
      : null;

  const hasAllowedRestrictions = !!allowedLabelValues && Object.keys(allowedLabelValues).length > 0;

  const applyAllowedFilter = useCallback((valuesMap: Record<string, string[]>): Record<string, string[]> => {
    if (!allowedLabelValues || Object.keys(allowedLabelValues).length === 0) return valuesMap;
    const filtered: Record<string, string[]> = {};
    for (const label of Object.keys(valuesMap)) {
      if (!(label in allowedLabelValues)) continue;
      const allowed = allowedLabelValues[label];
      if (Array.isArray(allowed) && allowed.length > 0) {
        const allowedSet = new Set(allowed);
        filtered[label] = valuesMap[label].filter((v) => allowedSet.has(v));
      } else {
        filtered[label] = valuesMap[label];
      }
    }
    return filtered;
  }, [allowedLabelValues]);

  const fetchInitialLabels = useCallback(async (preselections: Record<string, string[]> = {}) => {
    if (!selectedServer) return;
    try {
      setLabelsLoading(true);
      const { start, end } = getTimeRangeMs();
      const response = await apiClient.get(`/il/logs/loki-labels/${selectedServer}?start=${start}&end=${end}`);
      const lokiLabels: string[] = (response.data || []).filter((l: string) => l !== '__name__');

      const visibleLabels = hasAllowedRestrictions
        ? lokiLabels.filter((l) => l in allowedLabelValues!)
        : lokiLabels;

      setAllLabels(visibleLabels);
      setActiveLabels(visibleLabels);

      const valuesPromises = visibleLabels.map(async (label) => {
        try {
          const valRes = await apiClient.post(
            `/il/logs/loki-label-values/${selectedServer}?label=${encodeURIComponent(label)}`,
            { selectedLabels: {}, start, end },
          );
          return { label, values: (valRes.data || []) as string[] };
        } catch {
          return { label, values: [] as string[] };
        }
      });

      const results = await Promise.all(valuesPromises);
      const rawMap: Record<string, string[]> = {};
      results.forEach(({ label, values }) => { rawMap[label] = values; });
      const newMap = applyAllowedFilter(rawMap);
      setOriginalLabelValues(newMap);

      // If we already have value preselections (e.g. derived from this server's
      // saved labelValueSelections), narrow the OTHER labels' value lists via a
      // series query so the dialog opens with values consistent with the active
      // scope — labels with empty saved arrays will then display only values
      // observed alongside the pre-selected labels.
      const hasPre = Object.values(preselections).some((v) => v.length > 0);
      if (hasPre) {
        try {
          const seriesRes = await apiClient.post(`/il/logs/loki-series/${selectedServer}`, {
            selectedLabels: preselections,
            start,
            end,
          });
          const rawSeriesMap: Record<string, string[]> = seriesRes.data || {};
          const seriesMap = applyAllowedFilter(rawSeriesMap);
          const merged: Record<string, string[]> = { ...newMap };
          visibleLabels.forEach((label) => {
            if (preselections[label] && preselections[label].length > 0) return;
            merged[label] = seriesMap[label] || [];
          });
          setLabelValuesMap(merged);
          const newDisabled = visibleLabels.filter((l) =>
            (!preselections[l] || preselections[l].length === 0)
            && (!seriesMap[l] || seriesMap[l].length === 0),
          );
          setDisabledLabels(newDisabled);
        } catch {
          setLabelValuesMap(newMap);
          setDisabledLabels([]);
        }
      } else {
        setLabelValuesMap(newMap);
        setDisabledLabels([]);
      }
    } catch {
      setAllLabels([]);
      setLabelValuesMap({});
      setOriginalLabelValues({});
      setDisabledLabels([]);
    } finally {
      setLabelsLoading(false);
    }
  }, [selectedServer, getTimeRangeMs, applyAllowedFilter, hasAllowedRestrictions, allowedLabelValues]);

  const fetchSeriesAndUpdate = useCallback(async (
    currentSelections: Record<string, string[]>,
    modifiedLabel: string,
  ) => {
    if (!selectedServer) return;
    const hasSelection = Object.values(currentSelections).some(v => v.length > 0);

    if (!hasSelection) {
      setLabelValuesMap({ ...originalLabelValues });
      setDisabledLabels([]);
      return;
    }

    try {
      setSeriesLoading(true);
      const { start, end } = getTimeRangeMs();
      const response = await apiClient.post(`/il/logs/loki-series/${selectedServer}`, {
        selectedLabels: currentSelections,
        start,
        end,
      });
      const rawSeriesMap: Record<string, string[]> = response.data || {};
      const seriesMap = applyAllowedFilter(rawSeriesMap);

      setLabelValuesMap((prev) => {
        const updated = { ...prev };
        allLabels.forEach((label) => {
          if (label === modifiedLabel) return;
          updated[label] = seriesMap[label] ? seriesMap[label] : [];
        });
        return updated;
      });

      const newDisabled = allLabels.filter((l) => !seriesMap[l] || seriesMap[l].length === 0);
      setDisabledLabels(newDisabled);
    } catch {
      // keep current values
    } finally {
      setSeriesLoading(false);
    }
  }, [selectedServer, getTimeRangeMs, allLabels, originalLabelValues, applyAllowedFilter]);

  React.useEffect(() => {
    hasInitializedRef.current = false;
    setAllLabels([]);
    setActiveLabels([]);
    setLabelValuesMap({});
    setOriginalLabelValues({});
    setDialogSelectedValues({});
    setDisabledLabels([]);
    setValueFilter('');
    lastModifiedLabelRef.current = '';
    setLineFilterOperator('');
    setLineFilterValue('');
  }, [selectedServer]);

  /**
   * Seed dialog Step 2 selections from this server's saved scope:
   *   - label has values in labelValueSelections   -> pre-select those values
   *   - label has empty array in labelValueSelections -> fall back to the
   *     user's last query (initialSelections); otherwise leave unselected so
   *     they can pick from the live values shown for that label.
   * Labels not present in labelValueSelections are dropped because they're
   * already filtered out of Step 1 by `visibleLabels`.
   */
  const computeInitialDialogSelections = useCallback((): Record<string, string[]> => {
    if (!hasAllowedRestrictions || !allowedLabelValues) {
      return { ...initialSelections };
    }
    const initial: Record<string, string[]> = {};
    for (const [label, vals] of Object.entries(allowedLabelValues)) {
      if (Array.isArray(vals) && vals.length > 0) {
        initial[label] = [...vals];
      } else if (initialSelections[label] && initialSelections[label].length > 0) {
        initial[label] = [...initialSelections[label]];
      }
    }
    return initial;
  }, [hasAllowedRestrictions, allowedLabelValues, initialSelections]);

  React.useEffect(() => {
    if (open) {
      // Sync the time-range tracker so opening the dialog doesn't trigger the
      // change-driven refetch below in addition to the open refetch.
      prevTimeRangeKeyRef.current = timeRangeKey;
      const initial = computeInitialDialogSelections();
      setDialogSelectedValues(initial);
      setValueFilter('');
      if (initialLineFilter) {
        setLineFilterOperator(initialLineFilter.operator);
        setLineFilterValue(initialLineFilter.value);
      } else {
        setLineFilterOperator('');
        setLineFilterValue('');
      }
      if (!hasInitializedRef.current || allLabels.length === 0) {
        hasInitializedRef.current = true;
        fetchInitialLabels(initial);
      } else {
        // Labels are cached for this server — just re-narrow the value lists
        // so reopening the dialog reflects the saved scope.
        fetchSeriesAndUpdate(initial, '');
      }
    }
  }, [open]);

  // When the time range changes while the dialog is open (via the in-dialog
  // picker), refetch labels/values for the new range while preserving the
  // user's current value selections.
  React.useEffect(() => {
    if (!open) return;
    if (prevTimeRangeKeyRef.current === timeRangeKey) return;
    prevTimeRangeKeyRef.current = timeRangeKey;
    hasInitializedRef.current = true;
    fetchInitialLabels(dialogSelectedValues);
  }, [timeRangeKey]);

  const handleLabelPillToggle = (label: string) => {
    setActiveLabels((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label]
    );
  };

  const handleValueClick = (label: string, value: string) => {
    const current = dialogSelectedValues[label] || [];
    let newValues: string[];
    if (current.includes(value)) {
      newValues = current.filter((v) => v !== value);
    } else {
      newValues = [...current, value];
    }

    const newSelections = { ...dialogSelectedValues };
    if (newValues.length === 0) {
      delete newSelections[label];
    } else {
      newSelections[label] = newValues;
    }
    setDialogSelectedValues(newSelections);
    lastModifiedLabelRef.current = label;
    fetchSeriesAndUpdate(newSelections, label);
  };

  const handleClear = () => {
    setDialogSelectedValues({});
    setDisabledLabels([]);
    setLabelValuesMap({ ...originalLabelValues });
    setActiveLabels([...allLabels]);
    lastModifiedLabelRef.current = '';
    setLineFilterOperator('');
    setLineFilterValue('');
  };

  const handleSubmit = () => {
    onSubmit({ ...dialogSelectedValues }, currentLineFilter);
  };

  const dialogHasAnySelection = Object.values(dialogSelectedValues).some(v => v.length > 0);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : 2, minHeight: isMobile ? 'auto' : 560 } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, pb: 1 }}>
        <Typography variant="h6" component="span" fontWeight={600} sx={{ flexShrink: 0 }}>
          Label browser
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, justifyContent: 'flex-end', minWidth: 0 }}>
          <Tooltip title="Labels and values shown below reflect this time range">
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              onClick={(e) => setTimeAnchorEl(e.currentTarget)}
              startIcon={<AccessTime sx={{ fontSize: 16 }} />}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                maxWidth: isMobile ? 200 : 320,
                '& .MuiButton-startIcon': { mr: 0.5 },
              }}
            >
              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {timeRangeLabel}
              </Box>
            </Button>
          </Tooltip>
          <IconButton onClick={onClose} size="small" edge="end">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2, pb: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {labelsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress size={32} variant="determinate" value={30} sx={consistentSpinnerSx} />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
              Loading labels from Loki server...
            </Typography>
          </Box>
        ) : allLabels.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="body2" color="text.secondary">
              No labels found. The Loki server may not have any data in the selected time range.
            </Typography>
          </Box>
        ) : (
          <>
            {/* Section 1: Label pills */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                1. Select labels to search in
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Which labels would you like to consider for your search?
              </Typography>
              <Box sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                flexWrap: 'wrap',
                gap: 0.75,
              }}>
                {allLabels.map((label) => {
                  const isActive = activeLabels.includes(label);
                  const isDisabled = disabledLabels.includes(label);
                  const count = (labelValuesMap[label] || []).length;
                  const displayLabel = count > 0 ? `${label} (${count})` : label;

                  return (
                    <Chip
                      key={label}
                      label={displayLabel}
                      size={isMobile ? 'medium' : 'small'}
                      clickable={!isDisabled}
                      onClick={() => !isDisabled && handleLabelPillToggle(label)}
                      color={isActive && !isDisabled ? 'secondary' : 'default'}
                      variant={isActive && !isDisabled ? 'filled' : 'outlined'}
                      sx={{
                        fontWeight: 600,
                        fontSize: isMobile ? '0.85rem' : '0.78rem',
                        opacity: isDisabled ? 0.45 : 1,
                        cursor: isDisabled ? 'default' : 'pointer',
                        justifyContent: isMobile ? 'flex-start' : 'center',
                      }}
                    />
                  );
                })}
              </Box>
            </Box>

            {/* Section 2: Value columns */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                2. Find values for the selected labels
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Choose the label values that you would like to use for the query.
                Use the search field to find values across selected labels.
              </Typography>

              <TextField
                size="small"
                fullWidth
                placeholder="Filter expression for values"
                value={valueFilter}
                onChange={(e) => setValueFilter(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" sx={{ color: 'text.disabled' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 1.5 }}
              />

              {seriesLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CircularProgress size={16} variant="determinate" value={30} sx={consistentSpinnerSx} />
                  <Typography variant="caption" color="text.secondary">Updating labels...</Typography>
                </Box>
              )}

              <Box sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                gap: 1,
                overflowX: isMobile ? 'hidden' : 'auto',
                pb: 1,
                minHeight: isMobile ? 'auto' : 200,
              }}>
                {activeLabels
                  .filter((label) => !disabledLabels.includes(label))
                  .map((label) => {
                    const values = labelValuesMap[label] || [];
                    const selected = dialogSelectedValues[label] || [];
                    const count = values.length;
                    const filtered = valueFilter
                      ? values.filter((v) => v.toLowerCase().includes(valueFilter.toLowerCase()))
                      : values;

                    return (
                      <Box
                        key={label}
                        sx={{
                          minWidth: isMobile ? 'auto' : 160,
                          maxWidth: isMobile ? '100%' : 220,
                          flex: isMobile ? 'none' : '0 0 auto',
                          width: isMobile ? '100%' : 'auto',
                          border: '1px solid', borderColor: 'divider',
                          borderRadius: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden',
                        }}
                      >
                        <Box sx={{
                          px: 1, py: 0.5,
                          borderBottom: '1px solid', borderColor: 'divider',
                          backgroundColor: 'secondary.main',
                          color: (theme) => theme.palette.secondary.contrastText,
                        }}>
                          <Typography variant="caption" fontWeight={700} noWrap>
                            {label} ({count})
                          </Typography>
                        </Box>

                        <Box sx={{ flex: 1, maxHeight: 240, overflowY: 'auto' }}>
                          {filtered.map((value) => {
                            const isSelected = selected.includes(value);
                            return (
                              <Box
                                key={value}
                                onClick={() => handleValueClick(label, value)}
                                sx={{
                                  px: 1, py: 0.4, cursor: 'pointer',
                                  fontSize: '0.8rem', fontFamily: 'monospace',
                                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                  backgroundColor: isSelected ? 'secondary.main' : 'transparent',
                                  color: (theme) => isSelected
                                    ? theme.palette.secondary.contrastText
                                    : theme.palette.text.primary,
                                  '&:hover': {
                                    backgroundColor: isSelected ? 'secondary.dark' : 'action.hover',
                                  },
                                }}
                              >
                                {value}
                              </Box>
                            );
                          })}
                          {filtered.length === 0 && (
                            <Typography variant="caption" color="text.disabled" sx={{ px: 1, py: 1, display: 'block' }}>
                              {valueFilter ? 'No matches' : 'No values'}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })}

                {activeLabels.filter((l) => !disabledLabels.includes(l)).length === 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      Select labels above to see their values
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Section 3: Line Operation Filter (optional) */}
            <Box sx={{ opacity: LINE_FILTER_ENABLED ? 1 : 0.6 }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                3. Line filter (optional)
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                {LINE_FILTER_ENABLED
                  ? 'Optionally filter log lines by their content.'
                  : 'Line filtering is currently disabled — the Loki server is not optimized for contains / does-not-contain line queries.'}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <FormControl size="small" disabled={!LINE_FILTER_ENABLED} sx={{ minWidth: isMobile ? 'auto' : 300, width: isMobile ? '100%' : 'auto' }}>
                  <Select
                    value={lineFilterOperator}
                    disabled={!LINE_FILTER_ENABLED}
                    onChange={(e) => {
                      const val = e.target.value as LineFilterOperator | '';
                      setLineFilterOperator(val);
                      if (!val) setLineFilterValue('');
                    }}
                    displayEmpty
                    renderValue={(value) => {
                      if (!value) return <Typography color="text.secondary" variant="body2">No line filter</Typography>;
                      return <Typography variant="body2" sx={{ whiteSpace: 'normal', lineHeight: 1.4 }}>{LINE_FILTER_OPTIONS.find(o => o.value === value)?.label || value}</Typography>;
                    }}
                  >
                    <MenuItem value="">
                      <em>No line filter</em>
                    </MenuItem>
                    {LINE_FILTER_OPTIONS.map(opt => (
                      <MenuItem key={opt.value} value={opt.value} sx={{ whiteSpace: isMobile ? 'normal' : 'nowrap' }}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {LINE_FILTER_ENABLED && lineFilterOperator && (
                  <TextField
                    size="small"
                    placeholder={lineFilterOperator.includes('regex') ? 'Enter regex pattern...' : 'Enter text to match...'}
                    value={lineFilterValue}
                    onChange={(e) => setLineFilterValue(e.target.value)}
                    sx={{ flex: 1, minWidth: isMobile ? 'auto' : 200, width: isMobile ? '100%' : 'auto' }}
                  />
                )}
              </Box>
            </Box>

            {/* Section 4: Resulting selector */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                4. Resulting selector
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  px: 1.5, py: 1,
                  fontFamily: 'monospace', fontSize: '0.85rem', wordBreak: 'break-all',
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                }}
              >
                {buildSelectorString(dialogSelectedValues, currentLineFilter)}
              </Paper>
            </Box>
          </>
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
        <Button
          variant="contained"
          color="success"
          size="small"
          onClick={handleSubmit}
          disabled={labelsLoading || !dialogHasAnySelection}
          sx={{
            textTransform: 'none', fontWeight: 700,
            '&.Mui-disabled': {
              backgroundColor: (t) =>
                t.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
              color: (t) =>
                t.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.26)',
            },
          }}
        >
          Use selector
        </Button>

        {isMobile ? (
          <>
            <Tooltip title="Clear selections">
              <span>
                <IconButton
                  size="medium"
                  onClick={handleClear}
                  disabled={labelsLoading || !dialogHasAnySelection}
                  sx={{
                    border: '1px solid', borderColor: 'secondary.main', color: 'secondary.main',
                    borderRadius: 1, p: 1,
                    '&.Mui-disabled': { borderColor: 'divider', color: 'text.disabled' },
                  }}
                >
                  <DeleteSweep sx={{ fontSize: 22 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Cancel">
              <IconButton
                size="medium"
                onClick={onClose}
                sx={{
                  border: '1px solid', borderColor: 'secondary.main', color: 'secondary.main',
                  borderRadius: 1, p: 1,
                }}
              >
                <Cancel sx={{ fontSize: 22 }} />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <>
            <Button
              variant="contained"
              color="secondary"
              size="small"
              onClick={handleClear}
              startIcon={<DeleteSweep />}
              disabled={labelsLoading || !dialogHasAnySelection}
              sx={{
                textTransform: 'none',
                '&.Mui-disabled': {
                  backgroundColor: (t) =>
                    t.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
                  color: (t) =>
                    t.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.26)',
                },
              }}
            >
              Clear
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              onClick={onClose}
              startIcon={<Cancel />}
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </Button>
          </>
        )}
      </DialogActions>

      <TimePickerPopover
        anchorEl={timeAnchorEl}
        onClose={() => setTimeAnchorEl(null)}
        timeRangeLabel={timeRangeLabel}
        onQuickRangeSelect={(r) => {
          onQuickRangeSelect(r);
          setTimeAnchorEl(null);
        }}
        onApplyCustomRange={(fromMs, toMs, label) => {
          onApplyCustomRange(fromMs, toMs, label);
          setTimeAnchorEl(null);
        }}
        initialFrom={getPickerInitialDates().from}
        initialTo={getPickerInitialDates().to}
        tzName={tzName}
        tzString={tzString}
      />
    </Dialog>
  );
});

LabelBrowserDialog.displayName = 'LabelBrowserDialog';
